/**
 * @file This file contains the TooltipManager class for creating and managing tooltips.
 * @module TooltipManager
 */

import { tooltipsConfig } from '../config/tooltipsConfig.js';

export class TooltipManager {
    /**
     * @param {object} dependencies - The dependencies for the manager.
     * @param {HTMLElement} dependencies.tooltipOverlay - The DOM element to render tooltips into.
     * @param {import('./PlayerStatsManager.js').PlayerStatsManager} dependencies.playerStatsManager - The player stats manager instance.
     * @param {import('../lib/EventEmitter.js').EventEmitter} dependencies.eventEmitter - The global event emitter.
     */
    constructor({ tooltipOverlay, playerStatsManager, eventEmitter }) {
        this.overlay = tooltipOverlay;
        this.playerStatsManager = playerStatsManager;
        this.eventEmitter = eventEmitter;
        this.activeTooltips = new Map();

        // Global listener to handle closing tooltips via 'X' button or clicking away
        this.overlay.addEventListener('click', (e) => {
            if (e.target.matches('.tooltip-close-btn')) {
                const tooltipId = e.target.dataset.tooltipId;
                this.hide(tooltipId);
                return;
            }

            // If the click is on the overlay but not inside any tooltip, dismiss click-away tooltips
            if (e.target === this.overlay) {
                for (const [id, tooltipData] of this.activeTooltips.entries()) {
                    if (tooltipData.config.dismiss?.clickAway) {
                        this.hide(id);
                    }
                }
            }
        });
    }

    /**
     * Shows a tooltip based on its configuration.
     * @param {string} tooltipId - The ID of the tooltip from the config.
     * @param {string} targetSelector - A CSS selector for the element the tooltip should point to.
     */
    show(tooltipId, targetSelector) {
        const config = tooltipsConfig[tooltipId];
        if (!config) {
            console.warn(`Tooltip config not found for: ${tooltipId}`);
            return;
        }

        if (config.showOnce && this.playerStatsManager.hasSeenTooltip(tooltipId)) {
            return;
        }

        const targetElement = document.querySelector(targetSelector);
        if (!targetElement) {
            console.warn(`Tooltip target not found for: ${targetSelector}`);
            return;
        }

        const tooltipElement = this._createTooltipElement(config);
        this.overlay.appendChild(tooltipElement);
        this._positionTooltip(tooltipElement, targetElement, config.position);
        
        const dismissers = [];

        // Set up automatic dismissal based on config
        if (config.dismiss) {
            if (config.dismiss.action) {
                const actionListener = () => this.hide(tooltipId);
                this.eventEmitter.once(config.dismiss.action, actionListener);
                dismissers.push({ type: 'action', event: config.dismiss.action, listener: actionListener });
            }
            if (config.dismiss.duration) {
                const timeoutId = setTimeout(() => this.hide(tooltipId), config.dismiss.duration);
                dismissers.push({ type: 'timeout', id: timeoutId });
            }
        }

        this.activeTooltips.set(tooltipId, { element: tooltipElement, config, dismissers });

        if (config.showOnce) {
            this.playerStatsManager.setTooltipSeen(tooltipId);
        }
    }

    /**
     * Hides and removes a specific tooltip.
     * @param {string} tooltipId - The ID of the tooltip to hide.
     */
    hide(tooltipId) {
        const tooltipData = this.activeTooltips.get(tooltipId);
        if (tooltipData) {
            // Clean up any active dismiss listeners or timers
            tooltipData.dismissers.forEach(d => {
                if (d.type === 'action') {
                    this.eventEmitter.off(d.event, d.listener);
                }
                if (d.type === 'timeout') clearTimeout(d.id);
            });

            tooltipData.element.remove();
            this.activeTooltips.delete(tooltipId);
        }
    }

    _createTooltipElement(config) {
        const element = document.createElement('div');
        element.className = `tooltip tooltip-${config.position || 'bottom'}`;
        element.innerHTML = `
            <div class="tooltip-content">${config.content}</div>
            ${(config.dismiss?.showCloseButton !== false) 
        ? `<button class="tooltip-close-btn" data-tooltip-id="${config.id}">&times;</button>` 
        : ''
}
        `;
        return element;
    }

    _positionTooltip(tooltip, target, position) {
        const targetRect = target.getBoundingClientRect();
        const overlayRect = this.overlay.getBoundingClientRect();

        // Position relative to the viewport first, then adjust for overlay offset
        let top = 0;
        let left = 0;

        switch (position) {
        case 'top':
            top = targetRect.top - tooltip.offsetHeight - 15;
            left = targetRect.left + (targetRect.width / 2) - (tooltip.offsetWidth / 2);
            break;
            // ... other positions (left, right) can be added here
        case 'bottom':
        default:
            top = targetRect.bottom + 15;
            left = targetRect.left + (targetRect.width / 2) - (tooltip.offsetWidth / 2);
            break;
        }

        tooltip.style.top = `${top - overlayRect.top}px`;
        tooltip.style.left = `${left - overlayRect.left}px`;
    }
}